/**
 * Shared UI state for the Floor Management screen (Figma 2325:76849).
 *
 * Per the state rules (frontend-conventions §9) drawer flags and cross-surface
 * selections live in Redux: the header opens the drawers, the plan canvas
 * toggles the table selection and the drawer footers read it, so no single
 * component owns it.
 *
 * Deliberately holds **no** floor data. The plan and staff list are a server
 * read (`GET pos/api/v1/tables/`) and travel as props from the page — putting
 * them here would mean an Initializer round trip and a frame of empty canvas for
 * no benefit. Merges are server state too, carried on each table's own `merge`.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/** Which drawer, if any, is covering the screen. */
export type FloorDrawer =
  | "none"
  | "merge-tables"
  | "assign-staff"
  | "table-details";

/** Merge Tables is two steps: pick the tables, then confirm the merge. */
export type MergeStep = 1 | 2;

export interface FloorManagementState {
  activeDrawer: FloorDrawer;
  mergeStep: MergeStep;
  /** Tables ticked for a merge or a staff assignment. */
  selectedTableIds: string[];
  /**
   * The waiter picked in the assignment panel.
   *
   * **At most one.** A table takes a single `assigned_staff`, and the bulk route
   * is one waiter over many tables — there is no shape in this API that accepts
   * two people on one table, so the picker behaves like a radio group even
   * though it draws checkboxes.
   */
  selectedStaffIds: string[];
  /**
   * Guests seated at the unit being merged, as typed.
   *
   * ⚠️ **`POST tables/merges/` requires `guest_count`** — verified live on the
   * dev tenant: omitting it is a 400 `validation_error` with
   * `details.guest_count: ["This field is required."]`, although the guide's
   * prose reads as though it were optional (see the 2026-08-09 api-req).
   * The design draws no field for it, so the drawer adds one.
   *
   * A string, not a number: the field starts empty and "" is not 0.
   */
  mergeGuestCount: string;
}

const initialState: FloorManagementState = {
  activeDrawer: "none",
  mergeStep: 1,
  selectedTableIds: [],
  selectedStaffIds: [],
  mergeGuestCount: "",
};

const floorManagementSlice = createSlice({
  name: "floorManagement",
  initialState,
  reducers: {
    /** Opens a drawer with a clean selection — drawers never inherit one. */
    openFloorDrawer(state, action: PayloadAction<FloorDrawer>) {
      state.activeDrawer = action.payload;
      state.mergeStep = 1;
      state.selectedTableIds = [];
      state.selectedStaffIds = [];
      state.mergeGuestCount = "";
    },

    /**
     * Opens the details drawer for one table, pre-selecting it and the waiter
     * already covering it — this drawer edits an existing assignment rather
     * than starting a fresh one, so unlike `openFloorDrawer` it seeds state.
     */
    openTableDetails(
      state,
      action: PayloadAction<{ tableId: string; staffId: string | null }>,
    ) {
      state.activeDrawer = "table-details";
      state.mergeStep = 1;
      state.selectedTableIds = [action.payload.tableId];
      state.selectedStaffIds = action.payload.staffId
        ? [action.payload.staffId]
        : [];
    },

    closeFloorDrawer(state) {
      state.activeDrawer = "none";
      state.mergeStep = 1;
      state.selectedTableIds = [];
      state.selectedStaffIds = [];
      state.mergeGuestCount = "";
    },

    setMergeGuestCount(state, action: PayloadAction<string>) {
      state.mergeGuestCount = action.payload;
    },

    setMergeStep(state, action: PayloadAction<MergeStep>) {
      state.mergeStep = action.payload;
    },

    toggleTableSelection(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.selectedTableIds = state.selectedTableIds.includes(id)
        ? state.selectedTableIds.filter((selected) => selected !== id)
        : [...state.selectedTableIds, id];
    },

    /**
     * Picks the waiter, or clears the pick when the same one is chosen twice.
     *
     * Replaces rather than appends — see `selectedStaffIds`. Clearing matters:
     * it is how a table is un-staffed, which the API allows even on a withdrawn
     * table so a waiter is never stranded.
     */
    selectStaffMember(state, action: PayloadAction<string>) {
      state.selectedStaffIds = state.selectedStaffIds.includes(action.payload)
        ? []
        : [action.payload];
    },
  },
});

export const {
  openFloorDrawer,
  openTableDetails,
  closeFloorDrawer,
  setMergeStep,
  setMergeGuestCount,
  toggleTableSelection,
  selectStaffMember,
} = floorManagementSlice.actions;

export default floorManagementSlice.reducer;
